// src/components/BankPosting/searchUtils.js
// ============================================================================
// BLOCK BP.SEARCH.001 — Unified Bank Posting search helpers
// ----------------------------------------------------------------------------
// PURPOSE
// - Keeps local search behavior consistent across Bank Posting panels
// - Supports targeted invoice / amount search and plain text fallback
// - Uses space-separated AND terms, with optional comma-separated OR groups
// - Supports inclusive numeric ranges for power users
// - Prevents each component from re-implementing its own search rules
//
// SUPPORTED EXAMPLES
// - 0442587 0445511             => invoice AND search
// - 0442587,0445511             => invoice OR search
// - 0442587to0442677            => invoice inclusive range
// - in:0442587 0445511          => explicit invoice AND search
// - in:0442587,0445511          => explicit invoice OR search
// - balance:450 475             => amount AND search
// - balance:450,475             => amount OR search
// - balance:450to800            => amount inclusive range
// - amount:450to800             => amount inclusive range
// ============================================================================

// ============================================================================
// BLOCK BP.SEARCH.001.01 — Primitive string and invoice helpers
// ----------------------------------------------------------------------------
// PURPOSE
// - Normalize raw values before parsing or matching
// - Keep invoice-number comparison logic aligned with backend helpers
// ============================================================================
function trimStr(value) {
  return String(value ?? '').trim();
}

export function normalizeDisplayInvoiceNumber(value) {
  const raw = trimStr(value);
  if (!raw) return '';

  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  if (digits.length >= 7) {
    return digits.slice(-7);
  }

  return digits.padStart(7, '0');
}

function toNormalizedText(value) {
  return trimStr(value).toLowerCase();
}

function toFiniteNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function escapeRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeRangeSeparators(value) {
  return trimStr(value).replace(/\s*(?:to|\.\.|-)\s*/gi, 'to');
}

function splitSearchTokens(value) {
  return trimStr(value)
    .split(/[\s,]+/)
    .filter(Boolean);
}

function splitSearchGroups(value) {
  return trimStr(value)
    .split(',')
    .map((group) => trimStr(group))
    .filter(Boolean)
    .map((group) =>
      group
        .split(/\s+/)
        .map((token) => trimStr(token))
        .filter(Boolean)
    )
    .filter((group) => group.length > 0);
}

function stripSearchPrefix(token) {
  return trimStr(token).replace(/^(?:in|invoice|inv|balance|amount):/i, '');
}

// ============================================================================
// BLOCK BP.SEARCH.001.02 — Query shape detection helpers
// ----------------------------------------------------------------------------
// PURPOSE
// - Detect whether a raw query should be treated as invoice-oriented,
//   amount-oriented, or plain text
// ============================================================================
function extractPrefixedSegment(raw, prefixes) {
  const joinedPrefixes = prefixes.map(escapeRegex).join('|');
  const match = trimStr(raw).match(new RegExp(`^(?:${joinedPrefixes}):(.*)$`, 'i'));
  return match ? trimStr(match[1]) : '';
}

function parseNumericOrListSegment(segment, kind) {
  const raw = normalizeRangeSeparators(segment);
  if (!raw) return null;

  const tokenGroups = splitSearchGroups(raw)
    .map((group) => group.map(stripSearchPrefix).filter(Boolean))
    .filter((group) => group.length > 0);

  if (!tokenGroups.length) return null;

  const rangeMatch = raw.match(/^(\d+(?:\.\d+)?)to(\d+(?:\.\d+)?)$/i);
  if (rangeMatch) {
    const startRaw = rangeMatch[1];
    const endRaw = rangeMatch[2];

    if (kind === 'invoice') {
      const start = Number(normalizeDisplayInvoiceNumber(startRaw));
      const end = Number(normalizeDisplayInvoiceNumber(endRaw));

      if (Number.isFinite(start) && Number.isFinite(end)) {
        return {
          mode: 'invoice-range',
          raw,
          min: Math.min(start, end),
          max: Math.max(start, end),
        };
      }
    }

    if (kind === 'amount') {
      const start = Number(startRaw);
      const end = Number(endRaw);

      if (Number.isFinite(start) && Number.isFinite(end)) {
        return {
          mode: 'amount-range',
          raw,
          min: Math.min(start, end),
          max: Math.max(start, end),
        };
      }
    }
  }

  if (kind === 'invoice') {
    const invoiceTermGroups = tokenGroups
      .map((group) => [
        ...new Set(group.map((token) => normalizeDisplayInvoiceNumber(token)).filter(Boolean)),
      ])
      .filter((group) => group.length > 0);

    if (invoiceTermGroups.length) {
      return {
        mode: 'invoice-list',
        raw,
        invoiceTermGroups,
      };
    }
  }

  if (kind === 'amount') {
    const amountTermGroups = tokenGroups
      .map((group) => [
        ...new Set(
          group
            .map((token) => token.replace(/^\$/, ''))
            .map((token) => toFiniteNumber(token))
            .filter((token) => token !== null)
            .map((token) => Number(token.toFixed(2)))
        ),
      ])
      .filter((group) => group.length > 0);

    if (amountTermGroups.length) {
      return {
        mode: 'amount-list',
        raw,
        amountTermGroups,
      };
    }
  }

  return null;
}

function invoiceFragmentMatchesAny(invoiceFields, fragment) {
  const normalizedFragment = trimStr(fragment).replace(/\D/g, '');

  if (!normalizedFragment) {
    return false;
  }

  return (invoiceFields || []).some((field) => {
    const candidate = normalizeDisplayInvoiceNumber(field);
    return Boolean(candidate) && candidate.includes(normalizedFragment);
  });
}

// ============================================================================
// BLOCK BP.SEARCH.001.03 — Search query parser
// ----------------------------------------------------------------------------
// PURPOSE
// - Converts raw user input into a small parsed instruction object
// - Adds power-user invoice and amount list/range syntax
// ============================================================================
function parseSearchQuery(search) {
  const raw = trimStr(search);
  if (!raw) {
    return {
      mode: 'empty',
      raw,
    };
  }

  const explicitInvoiceSegment = extractPrefixedSegment(raw, ['in', 'invoice', 'inv']);
  if (explicitInvoiceSegment) {
    const invoiceQuery = parseNumericOrListSegment(explicitInvoiceSegment, 'invoice');
    if (invoiceQuery) return invoiceQuery;
  }

  const explicitAmountSegment = extractPrefixedSegment(raw, ['balance', 'amount']);
  if (explicitAmountSegment) {
    const amountQuery = parseNumericOrListSegment(explicitAmountSegment, 'amount');
    if (amountQuery) return amountQuery;
  }

  const normalizedRaw = normalizeRangeSeparators(raw);
  const implicitInvoiceQuery = parseNumericOrListSegment(normalizedRaw, 'invoice');
  if (implicitInvoiceQuery) {
    const tokens = splitSearchTokens(normalizedRaw);
    const allInvoiceLike = tokens.every((token) => /^\d{6,}$/.test(stripSearchPrefix(token)));
    const implicitRangeMatch = normalizedRaw.match(/^(\d+(?:\.\d+)?)to(\d+(?:\.\d+)?)$/i);
    const invoiceRangeLike = implicitRangeMatch
      ? [implicitRangeMatch[1], implicitRangeMatch[2]].every(
          (part) => part.replace(/\D/g, '').length >= 6
        )
      : false;
    if (allInvoiceLike || invoiceRangeLike) {
      return implicitInvoiceQuery;
    }
  }

  const implicitAmountQuery = parseNumericOrListSegment(normalizedRaw, 'amount');
  if (implicitAmountQuery) {
    const tokens = splitSearchTokens(normalizedRaw);
    const allAmountLike = tokens.every((token) =>
      /^\$?-?\d+(?:\.\d+)?$/.test(stripSearchPrefix(token))
    );
    if (allAmountLike || implicitAmountQuery.mode === 'amount-range') {
      return implicitAmountQuery;
    }
  }

  return {
    mode: 'text',
    raw,
    textNeedleGroups: splitSearchGroups(raw)
      .map((group) => group.map((token) => token.toLowerCase()).filter(Boolean))
      .filter((group) => group.length > 0),
  };
}

// ============================================================================
// BLOCK BP.SEARCH.001.04 — Amount and invoice token helpers
// ----------------------------------------------------------------------------
// PURPOSE
// - Support exact-list and inclusive-range matching for invoice numbers/amounts
// ============================================================================
function normalizeInvoiceCandidates(invoiceFields) {
  return (invoiceFields || []).map((field) => normalizeDisplayInvoiceNumber(field)).filter(Boolean);
}

function normalizeAmountCandidates(amountFields) {
  return (amountFields || [])
    .map((field) => toFiniteNumber(field))
    .filter((field) => field !== null)
    .map((field) => Number(field.toFixed(2)));
}

function matchesAmount(amountFields, parsed) {
  const amountCandidates = normalizeAmountCandidates(amountFields);
  if (!amountCandidates.length) return false;

  if (parsed.mode === 'amount-range') {
    return amountCandidates.some((value) => value >= parsed.min && value <= parsed.max);
  }

  if (parsed.mode === 'amount-list') {
    const groups = Array.isArray(parsed.amountTermGroups) ? parsed.amountTermGroups : [];

    return groups.some((group) => group.every((term) => amountCandidates.includes(term)));
  }

  return false;
}

function matchesInvoice(invoiceFields, parsed) {
  const invoiceCandidates = normalizeInvoiceCandidates(invoiceFields);
  if (!invoiceCandidates.length) return false;

  if (parsed.mode === 'invoice-range') {
    return invoiceCandidates.some((value) => {
      const asNumber = Number(value);
      return Number.isFinite(asNumber) && asNumber >= parsed.min && asNumber <= parsed.max;
    });
  }

  if (parsed.mode === 'invoice-list') {
    const groups = Array.isArray(parsed.invoiceTermGroups) ? parsed.invoiceTermGroups : [];

    return groups.some((group) =>
      group.every((term) => {
        const normalizedTerm = normalizeDisplayInvoiceNumber(term);
        return (
          invoiceCandidates.includes(normalizedTerm) ||
          invoiceFragmentMatchesAny(invoiceCandidates, normalizedTerm)
        );
      })
    );
  }

  return false;
}

// ============================================================================
// BLOCK BP.SEARCH.001.05 — Public unified search matcher
// ----------------------------------------------------------------------------
// PURPOSE
// - Provides one reusable entry point for local panel filtering
// - Caller supplies text / amount / invoice candidate fields
// - Matching behavior is chosen from the parsed user input mode
// ============================================================================
export function matchesUnifiedSearch({
  search,
  textFields = [],
  amountFields = [],
  invoiceFields = [],
}) {
  const normalizedSearch = trimStr(search);
  const parsed = parseSearchQuery(search);

  if (parsed.mode === 'empty') {
    return true;
  }

  if (/^\d+$/.test(normalizedSearch)) {
    const searchDigits = normalizedSearch.replace(/\D/g, '');
    const candidateFields = [...(invoiceFields || []), ...(textFields || [])];

    if (
      searchDigits &&
      candidateFields.some((field) => {
        const digits = trimStr(field).replace(/\D/g, '');
        return digits && digits.includes(searchDigits);
      })
    ) {
      return true;
    }
  }

  if (parsed.mode === 'amount-list' || parsed.mode === 'amount-range') {
    return matchesAmount(amountFields, parsed);
  }

  if (parsed.mode === 'invoice-list' || parsed.mode === 'invoice-range') {
    return matchesInvoice(invoiceFields, parsed);
  }

  const haystack = (textFields || []).map(toNormalizedText).filter(Boolean).join(' ');
  const groups = Array.isArray(parsed.textNeedleGroups) ? parsed.textNeedleGroups : [];

  if (!groups.length) {
    return true;
  }

  return groups.some((group) => group.every((token) => haystack.includes(token)));
}
